import { useState } from 'react';
import { AlertTriangle, Banknote, ListChecks, Loader2, X } from 'lucide-react';
import { useRefundablePayment, useRefundCancelledContract } from '@/features/contracts/hooks/useContracts';
import { formatCents } from '@/features/contracts/utils/refundLabels';

interface RefundContractModalProps {
  contractId: number;
  contractLabel: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Remboursement du dernier paiement d'un contrat déjà annulé, quand il ne l'a pas été
 * à l'annulation. Même flux que la modale d'annulation : un prélèvement encore en
 * cours est remboursé dès son encaissement, et la commission Affinicar peut être
 * rendue au vendeur. Un seul remboursement par contrat.
 */
export function RefundContractModal({ contractId, contractLabel, isOpen, onClose }: RefundContractModalProps) {
  const { mutate, isLoading } = useRefundCancelledContract();
  const {
    data: refundable,
    isInitialLoading: isChecking,
    isError: checkFailed,
  } = useRefundablePayment(contractId, isOpen);
  // Cochée par défaut, comme à l'annulation : rendre ce qui a été pris.
  const [refundCommission, setRefundCommission] = useState(true);

  // Le hook doit être appelé avant tout retour anticipé — règle des hooks React.
  if (!isOpen) return null;

  const payment = refundable?.payment ?? null;
  const commissionCents = payment?.commissionCents ?? 0;
  const willReturnCommission = commissionCents > 0 && refundCommission;

  const handleClose = () => {
    setRefundCommission(true);
    onClose();
  };

  const handleConfirm = () => {
    mutate({ id: contractId, refundCommission: willReturnCommission }, { onSuccess: () => handleClose() });
  };

  const recapitulatif: string[] = [];
  if (payment) {
    recapitulatif.push(
      payment.status === 'processing'
        ? `Le prélèvement de ${formatCents(payment.amountCents)} est encore en cours : il sera remboursé dès son encaissement.`
        : `Remboursement de ${formatCents(payment.amountCents)} au client.`,
    );
    recapitulatif.push(
      'Le remboursement part dès que le solde Stripe du vendeur le couvre ; ses virements sont suspendus en attendant et le client est prévenu par email.',
    );
    if (commissionCents > 0) {
      recapitulatif.push(
        willReturnCommission
          ? `Affinicar rend sa commission de ${formatCents(commissionCents)} au vendeur, une fois le remboursement effectif.`
          : `Affinicar conserve sa commission de ${formatCents(commissionCents)}.`,
      );
    }
  }

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-lg tw-w-full tw-max-h-[90vh] tw-overflow-hidden tw-flex tw-flex-col">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-5 tw-border-b tw-border-gray-200 tw-flex-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Banknote className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">Rembourser le dernier paiement</h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        <div className="tw-p-5 tw-space-y-4 tw-flex-1 tw-overflow-y-auto">
          <p className="tw-text-sm tw-text-gray-600 tw-m-0">
            Le contrat <strong>{contractLabel}</strong> est annulé. Vous allez rembourser au client son dernier
            paiement.
          </p>

          <div className="tw-border tw-border-gray-200 tw-rounded-md tw-p-3">
            <div className="tw-flex tw-items-start tw-gap-2">
              <Banknote className="tw-w-4 tw-h-4 tw-text-gray-600 tw-flex-shrink-0 tw-mt-0.5" />
              <div className="tw-flex-1 tw-space-y-2">
                {isChecking && (
                  <p className="tw-text-sm tw-text-gray-500 tw-m-0 tw-flex tw-items-center tw-gap-2">
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                    Recherche du dernier paiement…
                  </p>
                )}

                {!isChecking && checkFailed && (
                  <p className="tw-text-sm tw-text-amber-700 tw-m-0">
                    Impossible de vérifier les paiements Stripe. Réessayez plus tard.
                  </p>
                )}

                {!isChecking && !checkFailed && !payment && (
                  <p className="tw-text-sm tw-text-gray-600 tw-m-0">
                    Aucun paiement à rembourser : aucun paiement remboursable, ou un remboursement est déjà demandé.
                  </p>
                )}

                {payment && (
                  <>
                    <p className="tw-text-sm tw-text-gray-900 tw-m-0">
                      Dernier paiement : <strong>{formatCents(payment.amountCents)}</strong> du{' '}
                      {new Date(payment.paymentDate).toLocaleDateString('fr-FR')} (
                      {payment.status === 'processing' ? 'prélèvement en cours' : 'encaissé'})
                    </p>

                    {refundable && refundable.paymentsCount > 1 && (
                      <div className="tw-flex tw-items-start tw-gap-2 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-md tw-p-2">
                        <AlertTriangle className="tw-w-4 tw-h-4 tw-text-amber-600 tw-flex-shrink-0 tw-mt-0.5" />
                        <p className="tw-text-sm tw-text-amber-800 tw-m-0">
                          Ce contrat a {refundable.paymentsCount} paiements : seul le dernier sera remboursé.
                        </p>
                      </div>
                    )}

                    {commissionCents > 0 && (
                      <label className="tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-select-none">
                        <input
                          type="checkbox"
                          checked={refundCommission}
                          onChange={(e) => setRefundCommission(e.target.checked)}
                          disabled={isLoading}
                          className="tw-w-4 tw-h-4 tw-mt-0.5 tw-accent-affinicar-primary tw-cursor-pointer"
                        />
                        <span className="tw-text-sm tw-text-gray-900">
                          Rendre la commission Affinicar au vendeur ({formatCents(commissionCents)})
                        </span>
                      </label>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {recapitulatif.length > 0 && (
            <div className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-rounded-md tw-p-3">
              <div className="tw-flex tw-items-start tw-gap-2">
                <ListChecks className="tw-w-4 tw-h-4 tw-text-gray-700 tw-flex-shrink-0 tw-mt-0.5" />
                <div className="tw-flex-1">
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-m-0 tw-mb-2">
                    En confirmant, voici ce qui sera fait
                  </p>
                  <ul className="tw-text-sm tw-text-gray-700 tw-m-0 tw-pl-5 tw-space-y-1">
                    {recapitulatif.map((ligne) => (
                      <li key={ligne}>{ligne}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-5 tw-border-t tw-border-gray-200 tw-flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
          >
            Fermer
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading || !payment}
            className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-md hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {isLoading && <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />}
            Rembourser
          </button>
        </div>
      </div>
    </div>
  );
}
